import { useCallback, useMemo, useRef } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { StackableCols } from '../../styles';
import type {
  ActivityPassedDueSourceSubTypes,
  AllowedCheckOption,
  ActivityPassedDueVariableType,
  SourceVariableType,
  VariableValidation,
  VariableNameAndType,
} from '../../types';
import {
  useVariableDescriptionContext,
  type VariableErrorType,
} from '../../VariableWizardContext';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import { SelectNoScrollCapture as Select } from '__components/Inputs/Select';
import type {
  AutomationVariableDataType,
  Field,
  GenericOption,
} from 'pages/AutomationEngine/types';
import { fieldValidate, FieldValidation } from '../utilties';
import { useInputErrors } from '../useInputErrors';
import type { LoggedActivityValidationKeys } from '../ValidationKeys';
import { isEqual } from 'lodash';
import type { CheckFieldFromVariableCompatibilityFN } from '../../useVariableMetaData';
import { useActivityObjectOptions } from '../hooks/useActivityObjectOptions';
import { isUUIDcompatible } from '../../utilities';

type SourceType = GenericOption<ActivityPassedDueSourceSubTypes>;

const getValueSourceOptions = (t: TFunction): SourceType[] => [
  {
    value: 'assignee',
    label: t('Assignee (Employee)'),
  },
  {
    value: 'assignee_role',
    label: t('Assignee (Role)'),
  },
  {
    value: 'due_datetime',
    label: t('Due Date/Time'),
  },
  {
    value: 'record_association',
    label: t('Record Association'),
  },
  {
    value: 'scheduled_activity_id',
    label: t('Scheduled Activity ID'),
  },
  {
    value: 'activity_object_id',
    label: t('Activity Object ID'),
  },
];

const singleValueProperties: Field['properties'] = {
  allowAdd: false,
  allowMultiple: false,
  allowOptions: false,
  allowRelations: false,
  type: '',
  targetColumn: '',
};

const activityPassedDueAllowedTypeMappings: Record<
  ActivityPassedDueSourceSubTypes,
  AllowedCheckOption
> = {
  assignee: {
    kizenField: {
      fieldType: 'team_selector',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'compatibility',
  },
  assignee_role: {
    kizenField: {
      fieldType: 'relationship',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'uuid',
  },
  due_datetime: {
    kizenField: { fieldType: 'datetime', properties: singleValueProperties },
    allowedCheckTypes: 'compatibility',
  },
  scheduled_at: {
    kizenField: {
      fieldType: 'datetime',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'compatibility',
  },
  record_association: {
    kizenField: {
      fieldType: 'relationship',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'compatibility',
  },
  scheduled_activity_id: {
    kizenField: {
      fieldType: 'relationship',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'uuid',
  },
  activity_object_id: {
    kizenField: {
      fieldType: 'relationship',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'uuid',
  },
};

const optionFilter = (
  value: ActivityPassedDueSourceSubTypes | undefined,
  variable: VariableNameAndType,
  checkVariableFromFieldCompatibility: CheckFieldFromVariableCompatibilityFN
) => {
  if (!value || !variable) {
    return false;
  }

  const optionTypes = getSourceSubtypeAllowedTypes(value);

  if (optionTypes?.allowedCheckTypes === 'uuid') {
    return isUUIDcompatible(variable.dataType);
  }

  return optionTypes?.allowedCheckTypes === 'field'
    ? true
    : !!optionTypes?.kizenField &&
        checkVariableFromFieldCompatibility(
          {
            ...optionTypes.kizenField,
            relation:
              value === 'record_association' &&
              optionTypes.kizenField.fieldType === 'relationship'
                ? {
                    cardinality: 'many_to_one',
                    relatedObject: variable?.dataSubtype?.value!,
                  }
                : undefined,
          },
          variable
        );
};

const getSourceSubtypeAllowedTypes = (
  sourceSubtype: ActivityPassedDueSourceSubTypes | undefined
) =>
  sourceSubtype
    ? activityPassedDueAllowedTypeMappings[sourceSubtype]
    : undefined;

const getDefaultState = (
  sourceSubtype: ActivityPassedDueSourceSubTypes
): ActivityPassedDueVariableType => ({
  value: undefined,
  valueCategory: undefined,
  variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
  sourceSubtype,
  id: Date.now().toString(),
});

const showAssociatedObjectTypes: (
  | ActivityPassedDueSourceSubTypes
  | undefined
)[] = ['record_association'];

const useComponentState = (
  values: SourceVariableType & {
    sourceErrors: VariableErrorType;
  },
  t: TFunction
) => {
  const {
    variable,
    updateVariableSource,
    activityPastDueContext,
    checkVariableFromFieldCompatibility,
    errors,
    setErrors,
  } = useVariableDescriptionContext();

  const { dataType, dataSubtype } = variable;

  const valueSourceOptions = useMemo(() => {
    return getValueSourceOptions(t).filter(({ value }) =>
      optionFilter(value, variable, checkVariableFromFieldCompatibility)
    );
  }, [checkVariableFromFieldCompatibility, variable, t]);

  const valueSourceOptionsRef = useRef(valueSourceOptions);
  const dataSubtypeRef = useRef(dataSubtype);
  const dataTypeRef = useRef(dataType);

  const activityOptions = activityPastDueContext?.activities;

  const {
    activityPassedDueVariable = getDefaultState(valueSourceOptions[0]?.value),
    sourceErrors,
    id,
  } = values;

  const { sourceSubtype, valueCategory, value } = activityPassedDueVariable!;

  const {
    validate: validateSourceSubtype,
    hasError: hasErrorSourceSubtype,
    clearError: clearSourceSubtypeError,
  } = useInputErrors<LoggedActivityValidationKeys>(
    id,
    'sourceSubtype',
    sourceErrors,
    errors,
    setErrors
  );

  const {
    validate: validateValueCategory,
    hasError: hasErrorValueCategory,
    clearError: clearValueCategoryError,
  } = useInputErrors<LoggedActivityValidationKeys>(
    id,
    'valueCategory',
    sourceErrors,
    errors,
    setErrors
  );

  const {
    validate: validateValueValue,
    hasError: hasErrorValueValue,
    clearError: clearValueError,
  } = useInputErrors<LoggedActivityValidationKeys>(
    id,
    'value',
    sourceErrors,
    errors,
    setErrors
  );

  const { showActivitySelector, showAssociatedObject } = useMemo(
    () => ({
      showActivitySelector: !!sourceSubtype,
      showAssociatedObject:
        !!valueCategory && showAssociatedObjectTypes.includes(sourceSubtype),
    }),
    [sourceSubtype, valueCategory]
  );

  const updateActivityLoggedByVariable = useCallback(
    (activityPassedDueVariable: ActivityPassedDueVariableType) => {
      updateVariableSource({
        ...values,
        activityPassedDueVariable,
      });
    },
    [updateVariableSource, values]
  );

  const setSourceSubtype = useCallback(
    ({ value }: SourceType | { value: undefined }) => {
      updateActivityLoggedByVariable({
        ...activityPassedDueVariable,
        sourceSubtype: value,
        valueCategory: undefined,
        value: undefined,
      });
      clearSourceSubtypeError('sourceSubtype');
      clearValueCategoryError('valueCategory');
      clearValueError('value');
    },
    [
      clearSourceSubtypeError,
      clearValueCategoryError,
      clearValueError,
      activityPassedDueVariable,
      updateActivityLoggedByVariable,
    ]
  );

  const setActivityLogged = useCallback(
    (valueCategory: GenericOption) => {
      updateActivityLoggedByVariable({
        ...activityPassedDueVariable,
        valueCategory,
        value: undefined,
      });
      clearValueCategoryError('valueCategory');
      clearValueError('value');
    },
    [
      clearValueCategoryError,
      clearValueError,
      activityPassedDueVariable,
      updateActivityLoggedByVariable,
    ]
  );

  // used by record_association
  const setActivityValue = useCallback(
    (value: GenericOption | undefined) => {
      updateActivityLoggedByVariable({
        ...activityPassedDueVariable,
        value,
      });
      clearValueError('value');
    },
    [clearValueError, activityPassedDueVariable, updateActivityLoggedByVariable]
  );

  const getIntersection = (
    sourceSubtype: ActivityPassedDueSourceSubTypes | undefined,
    dataType: AutomationVariableDataType
  ) => {
    const optionTypes = getSourceSubtypeAllowedTypes(sourceSubtype);

    if (optionTypes?.allowedCheckTypes === 'uuid') {
      return isUUIDcompatible(dataType);
    }

    if (optionTypes?.allowedCheckTypes === 'field') {
      return true;
    }
    return (
      !!optionTypes?.kizenField &&
      checkVariableFromFieldCompatibility(
        {
          ...optionTypes.kizenField,
          relation:
            sourceSubtype === 'record_association' &&
            optionTypes.kizenField.fieldType === 'relationship'
              ? {
                  cardinality: 'many_to_one',
                  relatedObject: variable?.dataSubtype?.value!,
                }
              : undefined,
        },
        { dataType, isArray: variable.isArray }
      )
    );
  };

  // we need reset the config if the sourceSubtype chnages and it's no longer supported
  if (!isEqual(valueSourceOptions, valueSourceOptionsRef.current)) {
    const intersection = getIntersection(sourceSubtype, dataType);

    // if they don't intersect reset selection as it's in valid
    if (!intersection) {
      setSourceSubtype({ value: undefined });
    } else {
      if (dataType === 'entity') {
        if (
          // ensure value is cleared if it doesn't match the choosen dataSubtype (Object)
          dataType === 'entity' &&
          ['record_association'].includes(sourceSubtype!) &&
          typeof value === 'object' &&
          value?.entityId !== dataSubtype
        ) {
          setActivityValue(undefined);
        }
      } else {
        if (
          typeof value === 'object' &&
          !checkVariableFromFieldCompatibility(
            value.fieldType ? { fieldType: value.fieldType } : undefined,
            { dataType, isArray: variable.isArray }
          )
        ) {
          setActivityValue(undefined);
        }
      }
    }

    valueSourceOptionsRef.current = valueSourceOptions;
  }

  // whatch for changes in dataSubtype so we can clear value if it's not available
  if (dataSubtype !== dataSubtypeRef.current) {
    if (
      // ensure value is cleared if it doesn't match the chossen dataSubtype (Object)
      dataType === 'entity' &&
      ['record_association'].includes(sourceSubtype!) &&
      typeof value === 'object' &&
      value?.entityId !== dataSubtype
    ) {
      setActivityValue(undefined);
    }
    dataSubtypeRef.current = dataSubtype;
  }

  // watch for changes in dataType so we can clear value if it's not available
  if (
    dataType !== dataTypeRef.current &&
    dataType !== 'entity' &&
    typeof value === 'object' &&
    !checkVariableFromFieldCompatibility(
      value.fieldType ? { fieldType: value.fieldType } : undefined,
      { dataType, isArray: variable.isArray }
    )
  ) {
    // ensure value is cleared if it doesn't match the chosen dataSubtype (Object)
    setActivityValue(undefined);
  }

  dataTypeRef.current = dataType;

  const { activityObjectOptions, loadingActivityObjectOptions } =
    useActivityObjectOptions(valueCategory, dataType, dataSubtype);

  return {
    valueSourceOptions,
    sourceSubtype,
    setSourceSubtype,
    validateSourceSubtype,
    hasErrorSourceSubtype,
    showActivitySelector,
    activityOptions,
    valueCategory,
    setActivityLogged,
    validateValueCategory,
    hasErrorValueCategory,

    value,
    setActivityValue,
    validateValueValue,
    hasErrorValueValue,

    showAssociatedObject,
    loadingActivityObjectOptions,
    activityObjectOptions,
  };
};

export const Component = (
  values: SourceVariableType & {
    sourceErrors: VariableErrorType;
    dataType: AutomationVariableDataType;
    dataSubtype: any;
  }
) => {
  const { t } = useTranslation();

  const {
    valueSourceOptions,
    sourceSubtype,
    setSourceSubtype,
    validateSourceSubtype,
    hasErrorSourceSubtype,

    showActivitySelector,
    activityOptions,
    valueCategory,
    setActivityLogged,
    validateValueCategory,
    hasErrorValueCategory,

    value,
    setActivityValue,

    showAssociatedObject,
    loadingActivityObjectOptions,
    activityObjectOptions,
    validateValueValue,
    hasErrorValueValue,
  } = useComponentState(values, t);

  return (
    <StackableCols columns={3} gutter="20px">
      <Select
        label={t('Value Source')}
        placeholder={t('Choose Value Source')}
        value={valueSourceOptions.find(({ value }) => value === sourceSubtype)}
        options={valueSourceOptions}
        onChange={setSourceSubtype}
        validate={validateSourceSubtype}
        error={hasErrorSourceSubtype}
        menuInline
        data-qa="logged-activity-value-source"
      />

      {showActivitySelector ? (
        <Select
          label={t('Activity')}
          placeholder={t('Choose Activity')}
          value={valueCategory}
          options={activityOptions}
          onChange={setActivityLogged}
          validate={validateValueCategory}
          error={hasErrorValueCategory}
          menuInline
          data-qa="logged-activity-activity"
        />
      ) : null}

      {showAssociatedObject ? (
        <Select
          label={t('Associated Object')}
          placeholder={t('Choose Associated Object')}
          value={value}
          options={activityObjectOptions}
          onChange={setActivityValue}
          validate={validateValueValue}
          error={hasErrorValueValue}
          menuInline
          isLoading={loadingActivityObjectOptions}
          data-qa="logged-activity-associated-object"
        />
      ) : null}
    </StackableCols>
  );
};

const getFieldsToValidate = (t: TFunction): FieldValidation => ({
  sourceSubtype: [{ type: 'required', fieldType: 'entity', isSelect: true }],
  valueCategory: [
    { type: 'required', fieldType: 'entity', isSelect: true },
    {
      type: 'deleted',
      fieldType: 'entity',
      deletedConditions: ['entity', 'employee'],
      deletedNameLookup: { entity: t('Activity'), fallback: t('Record') },
    },
  ],
  value: [
    {
      type: 'required',
      fieldType: 'entity', // static mainly
      condition: {
        sourceSubtype: ['record_association'],
      },
      isSelect: true,
    },
    {
      type: 'deleted',
      fieldType: 'entity',
      deletedConditions: ['entity', 'employee'],
      deletedNameLookup: { entity: t('Activity Field'), fallback: t('Record') },
    },
  ],
});

export const validate = ({ values, checkRequired, t }: VariableValidation) => {
  if (values.sourceType === 'activity_past_due_trigger') {
    return fieldValidate(
      values.activityPassedDueVariable,
      getFieldsToValidate(t),
      undefined,
      checkRequired,
      t
    );
  }
  return { valid: false, errors: [] };
};
